/* global gtag, fbq */
import { useState, useEffect, useRef } from 'react';
import { Check, MapPin, Clock, Star, Users, Smartphone, Gift, Menu, X } from 'lucide-react';
import { ref, set } from 'firebase/database';
import { db } from '../firebaseConfig';
import { Helmet } from 'react-helmet-async';

function MissionSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes scanline {
          0% { top: -2px; opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes missionFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes headingGlow {
          0%, 100% { text-shadow: 0 0 8px rgba(197, 212, 160, 0.3); }
          50% { text-shadow: 0 0 20px rgba(197, 212, 160, 0.6), 0 0 40px rgba(197, 212, 160, 0.2); }
        }
        @keyframes cornerPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
      <section ref={sectionRef} className="py-16 sm:py-20 relative overflow-hidden" style={{ background: '#3d4a2d' }}>
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Chicago skyline SVG background */}
        <div className="absolute inset-0 flex items-end justify-center overflow-hidden" style={{ opacity: 0.12 }}>
          <img src="/images/chicago-grid.svg" alt="" className="w-full max-w-4xl h-auto" style={{ filter: 'brightness(1.5)' }} />
        </div>

        {/* Animated scanline */}
        <div
          className="absolute left-0 right-0 h-[2px] pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(197, 212, 160, 0.6), transparent)',
            animation: 'scanline 4s linear infinite',
            top: '-2px'
          }}
        />

        {/* Tactical corner brackets */}
        <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 pointer-events-none" style={{ borderColor: 'rgba(197, 212, 160, 0.4)', animation: 'cornerPulse 3s ease-in-out infinite' }} />
        <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 pointer-events-none" style={{ borderColor: 'rgba(197, 212, 160, 0.4)', animation: 'cornerPulse 3s ease-in-out infinite 0.5s' }} />
        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 pointer-events-none" style={{ borderColor: 'rgba(197, 212, 160, 0.4)', animation: 'cornerPulse 3s ease-in-out infinite 1s' }} />
        <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 pointer-events-none" style={{ borderColor: 'rgba(197, 212, 160, 0.4)', animation: 'cornerPulse 3s ease-in-out infinite 1.5s' }} />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* CLASSIFIED stamp */}
          <div
            className="inline-block mb-4 px-3 py-1 rounded text-xs font-bold tracking-[0.25em] uppercase"
            style={{
              color: 'rgba(248, 102, 43, 0.7)',
              border: '1px solid rgba(248, 102, 43, 0.35)',
              fontFamily: 'Rubik, sans-serif',
              opacity: isVisible ? 1 : 0,
              animation: isVisible ? 'missionFadeUp 0.6s ease-out forwards' : 'none',
            }}
          >
            Classified
          </div>

          <h2
            className="text-3xl sm:text-4xl font-bold mb-6"
            style={{
              fontFamily: 'Rubik, sans-serif',
              color: '#c5d4a0',
              animation: isVisible ? 'missionFadeUp 0.6s ease-out 0.15s both, headingGlow 3s ease-in-out 1s infinite' : 'none',
            }}
          >
            Your Mission
          </h2>

          <div style={{ opacity: isVisible ? 1 : 0, animation: isVisible ? 'missionFadeUp 0.6s ease-out 0.3s both' : 'none' }}>
            <p className="text-lg leading-relaxed mb-6" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              You're a spy on a mission to stop the theft of a piece of Chicago's history. Visit 12 iconic landmarks, solve riddles, and uncover a remarkable artifact hidden in plain sight.
            </p>
          </div>

        </div>
      </section>
    </>
  );
}

export default function BuyChicago() {
  const [purchaseType, setPurchaseType] = useState('self'); // 'self' or 'gift'
  const [giftTo, setGiftTo] = useState('');
  const [giftFrom, setGiftFrom] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [riddleAnswer, setRiddleAnswer] = useState('');
  const [riddleSolved, setRiddleSolved] = useState(false);
  const [riddleHints, setRiddleHints] = useState(0);
  const [riddleShake, setRiddleShake] = useState(false);
  const [demoStep, setDemoStep] = useState(0);


  // Show sticky CTA on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom = scrollY + window.innerHeight > document.documentElement.scrollHeight - 300;
      setShowStickyCTA(scrollY > 300 && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = async (location) => {
    // Track the click in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: 30.00,
        items: [{
          item_id: 'chicago-tour',
          item_name: 'Chicago Scavenger Hunt Tour',
          price: 30.00,
          quantity: 1
        }],
        purchase_type: purchaseType,
        cta_location: location
      });
    }
    // Track in Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_ids: ['chicago-tour'],
        content_type: 'product',
        value: 30.00,
        currency: 'USD'
      });
    }

    if (purchaseType === 'self') {
      // Regular purchase
      window.location.href = 'https://buy.stripe.com/eVq6oA0cK53a2hpbcZcZa04';
    } else {
      // Gift purchase - store personalization and redirect to gift link
      setIsProcessing(true);

      try {
        // Generate unique ID for this gift purchase intent
        const intentId = `gift_${Date.now()}_${Math.random().toString(36).substring(7)}`;

        // Store personalization data in Firebase temporarily
        await set(ref(db, `giftIntents/${intentId}`), {
          to: giftTo,
          from: giftFrom,
          message: giftMessage,
          createdAt: Date.now(),
          expiresAt: Date.now() + (60 * 60 * 1000) // Expires in 1 hour
        });

        // Redirect to gift Stripe link with intent ID
        window.location.href = `https://buy.stripe.com/6oU14ggbI7bi1dl0ylcZa05?client_reference_id=${intentId}`;
      } catch (error) {
        console.error('Error storing gift data:', error);
        console.error('Error details:', error.message, error.code);
        alert(`Failed to process gift purchase: ${error.message}\n\nPlease check the console for details or contact support.`);
        setIsProcessing(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Chicago Scavenger Hunt Tour - Interactive Walking Adventure</title>
        <meta name="description" content="Chicago scavenger hunt walking tour visiting 12 iconic landmarks. Solve riddles, discover hidden history. Self-paced adventure for $30. Start instantly!" />
        <meta name="keywords" content="Chicago scavenger hunt, Chicago walking tour, Chicago landmarks tour, Chicago treasure hunt, interactive Chicago tour, things to do in Chicago, Chicago Loop walking tour" />
        <link rel="canonical" href="https://play.tourinabox.com/buy/chicago" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://play.tourinabox.com/buy/chicago" />
        <meta property="og:title" content="Chicago Scavenger Hunt - Save Chicago's History" />
        <meta property="og:description" content="Visit 12 iconic Chicago landmarks. Solve riddles to save a piece of history from being stolen. Self-paced spy mission adventure. Perfect for groups & tourists." />
        <meta property="og:image" content="https://play.tourinabox.com/images/chicago.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Chicago architecture and skyline along Michigan Avenue" />
        <meta property="og:site_name" content="Tour in a Box" />
        <meta property="og:locale" content="en_US" />
        <meta property="product:price:amount" content="30.00" />
        <meta property="product:price:currency" content="USD" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://play.tourinabox.com/buy/chicago" />
        <meta name="twitter:title" content="Chicago Scavenger Hunt - Interactive Walking Tour" />
        <meta name="twitter:description" content="Visit 12 Chicago landmarks on a spy mission to save history. Solve riddles, explore iconic sites. Self-paced adventure in 1 hour." />
        <meta name="twitter:image" content="https://play.tourinabox.com/images/chicago.webp" />
        <meta name="twitter:image:alt" content="Chicago architecture and skyline along Michigan Avenue" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Chicago Scavenger Hunt Tour",
            "description": "Interactive walking tour visiting 12 iconic Chicago landmarks. Solve riddles on a spy mission to save a piece of history. Self-paced adventure through the Chicago Loop.",
            "brand": {
              "@type": "Brand",
              "name": "Tour in a Box"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://play.tourinabox.com/buy/chicago",
              "priceCurrency": "USD",
              "price": "30.00",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Tour in a Box LLC"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "image": "https://play.tourinabox.com/images/chicago.webp",
            "category": "Tours & Activities"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://play.tourinabox.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Chicago Tour",
                "item": "https://play.tourinabox.com/buy/chicago"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Header with Logo */}
      <header className="pt-8 pb-6 px-4 border-b border-gray-200 relative" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <img
            src="/PrimaryLogo.png"
            alt="Tour in a Box"
            className="h-8 sm:h-10"
            style={{ objectFit: 'contain', width: 'auto' }}
          />

          {/* Menu Button */}
          <div className="absolute right-4 top-8">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <Menu size={20} style={{ color: '#413C04' }} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                style={{ fontFamily: 'Urbanist, sans-serif' }}
              >
                <a
                  href="https://www.tourinabox.com/store"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Browse All Tours
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(255, 250, 223, 0.5))' }}>
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(/images/noise-512.png)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 sm:pt-8 sm:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Image - Shows first on mobile, second on desktop */}
            <div className="relative order-first md:order-last">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="/images/chicagoreel.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>

            {/* Copy - Shows second on mobile, first on desktop */}
            <div className="order-last md:order-first">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-0 uppercase" style={{ fontFamily: 'Rubik, sans-serif', color: '#FFFADF', WebkitTextStroke: '8px #413C04', paintOrder: 'stroke fill' }}>
                Chicago, Illinois
              </h1>

              <p className="text-2xl font-bold mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#6b8e5e' }}>
                Scavenger Hunt Tour
              </p>

              {/* Social Proof */}
              <div
                className="flex items-center gap-4 mb-6 cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => {
                  document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex -space-x-2">
                  <img src="/images/customer1.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer2.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer3.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <div className="flex items-center gap-1" style={{ color: '#F8662B' }}>
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                    </div>
                    <span className="font-semibold" style={{ color: '#413C04' }}>4.8 out of 5</span>
                  </div>

                </div>
              </div>

              <p className="text-lg mb-6" style={{ color: '#413C04', opacity: 0.75 }}>
                Chicago's landmarks hold the clues. Crack the codes before a piece of history is stolen.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>12 landmark stops, each with a riddle</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>1-hour self-paced adventure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Play instantly or anytime in the next year</span>
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2" style={{ borderColor: '#E4B4A6' }}>
                {/* Gift Personalization Form (conditional) */}
                {purchaseType === 'gift' && (
                  <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#FFFADF' }}>
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      <Gift size={16} />
                      Personalize Your Gift (Optional)
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          To
                        </label>
                        <input
                          type="text"
                          value={giftTo}
                          onChange={(e) => setGiftTo(e.target.value)}
                          placeholder="Recipient's name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          From
                        </label>
                        <input
                          type="text"
                          value={giftFrom}
                          onChange={(e) => setGiftFrom(e.target.value)}
                          placeholder="Your name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          Personal Message
                        </label>
                        <textarea
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          rows={3}
                          maxLength={200}
                          className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                        <p className="text-xs mt-1" style={{ color: '#385B37', opacity: 0.6, fontFamily: 'Urbanist, sans-serif' }}>
                          {giftMessage.length}/200 characters
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleBuyNow('hero')}
                  disabled={isProcessing}
                  className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: '#F8662B',
                    fontFamily: 'Rubik, sans-serif'
                  }}
                  onMouseEnter={(e) => !isProcessing && (e.currentTarget.style.background = '#E05520')}
                  onMouseLeave={(e) => !isProcessing && (e.currentTarget.style.background = '#F8662B')}
                >
                  {isProcessing ? 'Processing...' : (purchaseType === 'gift' ? '🎁 Continue to Checkout' : 'Get My Tour — $29.99')}
                </button>

                {/* Gift Toggle Link */}
                {purchaseType === 'self' ? (
                  <button
                    onClick={() => setPurchaseType('gift')}
                    className="w-full mt-3 py-1.5 text-sm transition-all flex items-center justify-center gap-1.5 hover:opacity-70"
                    style={{ color: '#413C04', opacity: 0.5, fontFamily: 'Rubik, sans-serif' }}
                  >
                    <Gift size={14} style={{ color: '#F8662B' }} />
                    <span>Buying as a gift?</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setPurchaseType('self');
                      setGiftTo('');
                      setGiftFrom('');
                      setGiftMessage('');
                    }}
                    className="w-full text-sm mt-2 py-1 transition-all hover:underline"
                    style={{ color: '#8FB3D3', fontFamily: 'Urbanist, sans-serif' }}
                  >
                    ← Back to regular purchase
                  </button>
                )}

                <div className="mt-4 flex items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm flex-wrap" style={{ color: '#413C04', opacity: 0.7 }}>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Check size={14} style={{ color: '#385B37' }} />
                    1 year access
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Check size={14} style={{ color: '#385B37' }} />
                    Instant access
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Check size={14} style={{ color: '#385B37' }} />
                    30-day refund
                  </span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Storyline */}
      <MissionSection />

      {/* Try It — Interactive Demo */}
      <section className="py-16" style={{ background: 'linear-gradient(to bottom, white, #FFFADF)' }}>
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Try a Sample Stop
          </h2>
          <p className="text-center mb-8" style={{ color: '#413C04', opacity: 0.7 }}>
            See how the tour works with a sample riddle
          </p>

          {/* Phone-style frame */}
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4" style={{ borderColor: '#413C04', background: '#413C04' }}>
            {/* Phone top bar */}
            <div className="flex items-center justify-between px-4 py-2" style={{ background: '#413C04' }}>
              <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>Current Location: Cloud Gate</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(248, 102, 43, 0.3)', color: '#F8662B' }}>Stop 3 of 12</span>
            </div>

            {/* Screen content */}
            <div className="bg-white px-5 py-6" style={{ minHeight: 320 }}>

              {/* Card 0: Riddle */}
              {demoStep === 0 && (
                <div>
                  <div className="overflow-hidden mb-4 -mx-5 -mt-6">
                    <img src="/images/chicago/the-bean.jpg" alt="Cloud Gate (The Bean)" className="w-full h-24 object-cover" style={{ objectPosition: 'center 60%' }} />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.12)' }}>
                      <Star size={16} style={{ color: '#F8662B' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>RIDDLE</span>
                  </div>
                  <p className="text-base leading-relaxed mb-4" style={{ color: '#413C04' }}>
                    A silver surface, smooth and round,<br />
                    Shows the sky and all around.<br />
                    Clouds and towers drift in me.<br />
                    What am I doing, unceasingly?
                  </p>

                  <div className={`mb-4 ${riddleShake ? 'animate-shake' : ''}`}>
                    <input
                      type="text"
                      value={riddleAnswer}
                      onChange={(e) => setRiddleAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const a = riddleAnswer.trim().toLowerCase();
                          if (a.includes('reflecting') || a.includes('reflection')) {
                            setRiddleSolved(true);
                            setDemoStep(1);
                          } else {
                            setRiddleShake(true);
                            setTimeout(() => setRiddleShake(false), 500);
                          }
                        }
                      }}
                      placeholder="Type your answer..."
                      className="w-full px-4 py-3 rounded-xl text-base outline-none"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', fontFamily: 'Urbanist, sans-serif' }}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const a = riddleAnswer.trim().toLowerCase();
                        if (a.includes('reflecting') || a.includes('reflection')) {
                          setRiddleSolved(true);
                          setDemoStep(1);
                        } else {
                          setRiddleShake(true);
                          setTimeout(() => setRiddleShake(false), 500);
                        }
                      }}
                      className="flex-1 text-white font-bold py-3 rounded-xl transition-all"
                      style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                    >
                      Submit
                    </button>
                    <button
                      onClick={() => setRiddleHints(h => Math.min(h + 1, 2))}
                      className="px-4 py-3 rounded-xl font-semibold transition-all"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', background: 'white' }}
                    >
                      {riddleHints === 0 ? 'Hint?' : riddleHints === 1 ? 'More?' : 'Last hint'}
                    </button>
                  </div>

                  {riddleHints >= 1 && (
                    <div className="mt-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 Look at the polished stainless steel surface. What is it doing to the sky and buildings?
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <div className="mt-2 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 Use the -ing form of the word for what a mirror does
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <button
                      onClick={() => {
                        setRiddleSolved(true);
                        setDemoStep(1);
                      }}
                      className="mt-3 text-sm font-semibold underline"
                      style={{ color: '#413C04', opacity: 0.5 }}
                    >
                      Reveal answer
                    </button>
                  )}
                </div>
              )}

              {/* Card 1: Solved! */}
              {demoStep === 1 && (
                <div>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                      <Check size={16} style={{ color: '#385B37' }} />
                    </div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#385B37' }}>
                      You got it!
                    </h3>
                  </div>
                  <p className="font-semibold mb-4 text-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Cloud Gate ("The Bean")</p>
                  <div className="mb-5">
                    <p className="text-sm font-bold uppercase tracking-wide mb-2" style={{ color: '#F8662B' }}>Learn about this site</p>
                    <ul className="text-base space-y-1.5" style={{ color: '#413C04', opacity: 0.7 }}>
                      <li>• Designed by artist Anish Kapoor, completed in 2006</li>
                      <li>• Made of 168 stainless steel plates weighing 110 tons</li>
                      <li>• Polished so perfectly that no seams are visible</li>
                      <li>• Most photographed piece of public art in America</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => setDemoStep(2)}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                  >
                    See what's next →
                  </button>
                </div>
              )}

              {/* Card 2: Directions preview */}
              {demoStep === 2 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                        <MapPin size={16} style={{ color: '#8FB3D3' }} />
                      </div>
                      <span className="text-sm font-bold" style={{ color: '#8FB3D3', fontFamily: 'Rubik, sans-serif' }}>DIRECTIONS TO STOP 4</span>
                    </div>
                    <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(143, 179, 211, 0.15)', color: '#6A92B3' }}>4 min walk</span>
                  </div>

                  <p className="text-lg mb-4 leading-relaxed" style={{ color: '#413C04' }}>
                    With Cloud Gate and the street to your back, take a left. Walk down the stairs and cross to the <strong>Chicago Cultural Center</strong>, a grand building with ornate bronze doors.
                  </p>

                  <button
                    onClick={() => handleBuyNow('demo')}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#385B37', fontFamily: 'Rubik, sans-serif' }}
                  >
                    Get the full 12-stop tour →
                  </button>
                  <button
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="w-full mt-2 text-sm font-semibold py-2 transition-colors"
                    style={{ color: '#413C04', opacity: 0.5 }}
                  >
                    Keep learning more ↓
                  </button>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 py-3" style={{ background: '#413C04' }}>
              {[0, 1, 2].map(i => (
                <button
                  key={i}
                  onClick={() => { if (i === 0 || riddleSolved) setDemoStep(i); }}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: demoStep === i ? 24 : 8,
                    height: 8,
                    background: demoStep === i ? '#F8662B' : 'rgba(255,255,255,0.3)',
                    cursor: (i === 0 || riddleSolved) ? 'pointer' : 'default',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Caption below phone */}
          <p className="text-center text-sm mt-6" style={{ color: '#413C04', opacity: 0.6 }}>
            {demoStep === 0 && !riddleSolved && 'Go ahead — try solving it!'}
            {demoStep === 1 && '12 stops, each with a unique riddle'}
            {demoStep === 2 && 'Navigate by landmarks, not GPS'}
            {demoStep === 0 && riddleSolved && 'You already solved it! Tap the dots to explore.'}
          </p>
        </div>

        {/* Shake animation */}
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-8px); }
            75% { transform: translateX(8px); }
          }
          .animate-shake { animation: shake 0.3s ease-in-out; }
        `}</style>
      </section>

      {/* Features Grid */}
      <section className="py-16" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Why Explorers Love Our Tours
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <MapPin size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Landmark-Based Directions</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Navigate using landmarks. You'll look up and truly notice the city
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                <Clock size={24} style={{ color: '#385B37' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Go At Your Pace</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Pause anytime for photos, food, or shopping. Your progress is saved.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Smartphone size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Works Offline</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Download once and use without internet connection
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Star size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Engaging Riddles</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Fun challenges at each stop with hints if you need them
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(228, 180, 166, 0.4)' }}>
                <Users size={24} style={{ color: '#E4B4A6' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Perfect for Everyone</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Great for couples, families, friends, or solo adventurers
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Gift size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Great for Gifting</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Select gift option for personalized, printable gift certificate
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16" style={{ background: 'linear-gradient(to bottom right, rgba(255, 250, 223, 0.6), rgba(228, 180, 166, 0.4))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            What Explorers Are Saying
          </h2>
          <p className="text-center mb-12" style={{ color: '#413C04', opacity: 0.7 }}>
            Real reviews from real Tour in a Box explorers
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved the scavenger hunt. What a great way to get out and see the city from a new perspective."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>AK</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Amber K.</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Verified Customer</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved it! We went as a family and it was excellent."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #385B37, #2A4428)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>DA</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>DelAguaPR</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Verified Customer · <a href="https://share.google/3OprWt8jPXPP6VqCk" target="_blank" rel="noopener noreferrer" className="hover:underline">Google Review</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Our group loved the scavenger-hunt feel of the tour. Great idea and product."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #F8662B, #E05520)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>NH</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Nancy H.</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Verified Customer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Comparison */}
      <section className="py-16" style={{ background: '#FFFADF' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            How We Compare
          </h2>
          <p className="text-center mb-10" style={{ color: '#413C04', opacity: 0.7 }}>
            See how we stack up against other Chicago experiences
          </p>

          <div className="rounded-2xl overflow-hidden shadow-lg border" style={{ borderColor: '#E4B4A6' }}>
            {/* Header row */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center text-sm font-bold" style={{ background: '#413C04', color: 'white', fontFamily: 'Rubik, sans-serif' }}>
              <div className="py-3 px-2"></div>
              <div className="py-3 px-2 relative" style={{ background: '#F8662B' }}>
                Tour in a Box
              </div>
              <div className="py-3 px-2">Walking Tour</div>
              <div className="py-3 px-2">Escape Room</div>
            </div>

            {/* Price row */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-left pl-4 flex flex-col justify-center" style={{ color: '#413C04' }}>
                <div className="text-sm font-semibold" style={{ fontFamily: 'Rubik, sans-serif' }}>Price</div>
                <div className="text-xs leading-tight" style={{ opacity: 0.5 }}>for group of 3</div>
              </div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}>
                <span className="text-lg font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>$30</span>
              </div>
              <div className="px-2 flex items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$105</span>
              </div>
              <div className="px-2 flex items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$130</span>
              </div>
            </div>

            {/* Private */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Private</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Your own pace */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>At your own pace</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Play anytime */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Play anytime</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Fun & educational */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white min-h-[52px]">
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Fun & educational</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="text-xs font-semibold" style={{ color: '#385B37' }}>Both</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Educational</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Fun</span></div>
            </div>
          </div>

          <p className="text-center text-sm mt-4" style={{ color: '#413C04', opacity: 0.6 }}>
            Groups of 3 or fewer typically share one tour
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Frequently Asked Questions
          </h2>

          <style>{`
            details summary {
              list-style: none;
              position: relative;
              padding-right: 2rem;
            }
            details summary::-webkit-details-marker {
              display: none;
            }
            details summary::after {
              content: '+';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              font-size: 1.5rem;
              font-weight: bold;
              color: #413C04;
              transition: transform 0.2s;
            }
            details[open] summary::after {
              content: '−';
            }
          `}</style>

          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Where does the tour start? Where does it visit?
              </summary>
              <div className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                <p className="mb-2">The tour starts at the Art Institute of Chicago and follows a route along Michigan Avenue that can be completed in about 1 hour. The 12 stops are:</p>
                <ol className="list-decimal list-inside space-y-0.5 mb-2 text-sm">
                  <li>Art Institute of Chicago</li>
                  <li>Crown Fountain</li>
                  <li>Cloud Gate</li>
                  <li>Chicago Cultural Center</li>
                  <li>Carbide & Carbon Building</li>
                  <li>London Guarantee Building</li>
                  <li>Fort Dearborn Site</li>
                  <li>Regeneration</li>
                  <li>DuSable Bridge</li>
                  <li>DuSable Bust</li>
                  <li>Wrigley Building</li>
                  <li>Tribune Tower</li>
                </ol>
              </div>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Do I need to download an app?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                No! The tour works directly in your web browser on any smartphone. You can also add it to your home screen for offline access.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Can multiple people use one purchase?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Each purchase unlocks access for one account. Groups of 3 or fewer usually share one device, but larger groups often prefer having access on multiple devices so everyone can play along.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What's your refund policy?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                We offer a 30-day money-back guarantee. If you're not satisfied with your tour experience, contact us for a full refund.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What languages is this available in?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour is currently available in English.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#F8662B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Ready for an Adventure?
          </h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join adventurers who've discovered Chicago in a whole new way
          </p>

          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto shadow-2xl">
            <div className="flex items-baseline justify-center gap-3 mb-4">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$29.99</span>
            </div>
            <button
              onClick={() => handleBuyNow('bottom')}
              className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 mb-4"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2A2702'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#413C04'}
            >
              Get My Tour
            </button>
            <div className="flex items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm flex-wrap" style={{ color: '#413C04', opacity: 0.7 }}>
              <span className="whitespace-nowrap">✓ 1 year access</span>
              <span className="whitespace-nowrap">✓ Instant access</span>
              <span className="whitespace-nowrap">✓ 30-day refund</span>
            </div>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Secure checkout powered by Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#413C04' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'rgba(255, 250, 223, 0.7)' }}>
              © {new Date().getFullYear()} Tour in a Box LLC
            </div>
            <div className="flex gap-6 text-sm">
              <a href="mailto:hello@tourinabox.com" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Contact
              </a>
              <a href="https://www.tourinabox.com/policies" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Privacy
              </a>
              <a href="https://www.instagram.com/unboxyourtour" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile-Only Sticky Bottom CTA */}
      {showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E4B4A6' }}>
          <div className="px-4 py-3">
            <button
              onClick={() => handleBuyNow('sticky')}
              className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
            >
              Get My Tour — $29.99
            </button>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>Secure checkout with Stripe</span>
              <span>•</span>
              <span>30-day refund</span>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}
